/**
 * SciChart-based Multi-Well Volume Chart.
 *
 * Renders production series for multiple wells on a single chart with optional
 * type curve overlay and cross-chart hover highlighting (same pattern as the
 * probit chart). Series appearance is controlled via the `seriesStyle` prop.
 *
 * Designed to be rendered one-per-stream in a grid (e.g. one chart for Oil,
 * another for Gas, etc.). The parent passes `selectedSeries` with a single
 * stream and provides a title via `header`.
 *
 * Supports chart types: monthly, logarithmic, cumulative, and ratio.
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { debounce } from 'lodash-es';
import {
  SciChartSurface,
  SciChartJSLightTheme,
  NumericAxis,
  LogarithmicAxis,
  FastLineRenderableSeries,
  XyDataSeries,
  MouseWheelZoomModifier,
  ZoomPanModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  EAxisAlignment,
  EAutoRange,
  ChartModifierBase2D,
  ModifierMouseArgs,
  IPointMetadata,
  TSciChart,
  NumericLabelProvider,
  NumberRange,
  Thickness,
  VerticalLineAnnotation,
} from 'scichart';

import {
  RatioRecord,
  TypeCurveRatioRecord,
  TypeCurveVolumeRecord,
  VolumeRecord,
} from 'types/api-responses';
import { MineralType } from 'types/minerals';
import type { XAxisType } from 'components/well-page-content/types';
import { X_AXIS_TYPE_LABELS } from 'components/well-page-content/decline-curve-analysis/x-axis-switcher';
import { monthlyVolumeToDaily } from 'components/well-page-content/decline-curve-analysis/volume-display-scaling';
import { formatNumber } from 'utils/number';
import {
  SciChartExportImageButton,
  createSciChartElementHooks,
} from 'components/chart-export';
import { ensureWasmConfigured } from 'components/charts/scichart/ensure-wasm-configured';
import { MonthlyTickProvider } from 'components/charts/scichart/monthly-tick-provider';
import style from './multi-well-volume-chart.module.scss';

// ---------------------------------------------------------------------------
// Staggered rebuild scheduler
// ---------------------------------------------------------------------------
const CHARTS_PER_FRAME = 4;
const pendingRebuilds = new Map<string, () => void>();
let scheduledRaf: number | null = null;

function flushRebuilds() {
  scheduledRaf = null;
  const entries = Array.from(pendingRebuilds.entries()).slice(
    0,
    CHARTS_PER_FRAME
  );
  for (const [id, fn] of entries) {
    pendingRebuilds.delete(id);
    fn();
  }
  if (pendingRebuilds.size > 0) {
    scheduledRaf = requestAnimationFrame(flushRebuilds);
  }
}

function scheduleChartRebuild(
  chartId: string,
  rebuildFn: () => void
): () => void {
  pendingRebuilds.set(chartId, rebuildFn);
  if (scheduledRaf === null) {
    scheduledRaf = requestAnimationFrame(flushRebuilds);
  }
  return () => {
    pendingRebuilds.delete(chartId);
  };
}

// ---------------------------------------------------------------------------
// Series style system
// ---------------------------------------------------------------------------

export interface SeriesStyleConfig {
  /** Hex color string, e.g. '#808080'. */
  color?: string;
  /** Stroke width in pixels. Defaults differ per series type. */
  lineWidth?: number;
  /** Line style. Defaults differ per series type. */
  style?: 'dash' | 'solid';
}

export interface MultiWellSeriesStyle {
  production?: SeriesStyleConfig;
  typeCurve?: SeriesStyleConfig;
  wellCount?: SeriesStyleConfig;
  aggregation?: SeriesStyleConfig;
  forecast?: SeriesStyleConfig;
}

type ResolvedSeriesStyle = Required<SeriesStyleConfig>;

const DEFAULT_SERIES_STYLE: Record<
  keyof MultiWellSeriesStyle,
  ResolvedSeriesStyle
> = {
  production: { color: '#c6c6c6', lineWidth: 2, style: 'solid' },
  typeCurve: { color: '#161616', lineWidth: 3.5, style: 'dash' },
  wellCount: { color: '#161616', lineWidth: 2, style: 'solid' },
  aggregation: { color: '#0062ff', lineWidth: 4, style: 'solid' },
  forecast: { color: '#42be65', lineWidth: 3, style: 'dash' },
};

function resolveSeriesStyle(
  key: keyof MultiWellSeriesStyle,
  overrides?: SeriesStyleConfig
): ResolvedSeriesStyle {
  return { ...DEFAULT_SERIES_STYLE[key], ...overrides };
}

/**
 * Returns the SciChart strokeDashArray for the given style, or undefined for solid.
 * @param {'dash'|'solid'} style - The line style.
 * @return {number[]|undefined} A dash pattern array for dashed lines, or undefined for solid.
 */
function getDashArray(style: 'dash' | 'solid'): number[] | undefined {
  return style === 'dash' ? [6, 4] : undefined;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WELL_COUNT_Y_AXIS_ID = 'wellCountAxis';
const DIMMED_OPACITY = 0.15;
const TYPE_CURVE_OPACITY = 1;
const HIT_TEST_RADIUS = 12;
const HOVER_STROKE_THICKNESS_DELTA = 2;

const PRIMARY_Y_AXIS_ID = 'primaryYAxis';
const SECONDARY_Y_AXIS_ID = 'secondaryYAxis';
const DEFAULT_X_AXIS_ID = 'defaultXAxis';

const STREAM_Y_AXIS_TITLES: Record<string, string> = {
  oil: 'Oil [bbl]',
  gas: 'Gas [Mcf]',
  water: 'Water [bbl]',
  gor: 'GOR [Mcf/bbl]',
  wor: 'WOR [bbl/bbl]',
  wgr: 'WGR [bbl/MMcf]',
  cgr: 'CGR [bbl/MMcf]',
  watercut: 'Watercut [%]',
};

const volumeMap: Record<
  string,
  'volume' | 'cumulative' | 'ratio' | 'materialBalanceTime'
> = {
  monthly: 'volume',
  logarithmic: 'volume',
  cumulative: 'cumulative',
  ratio: 'ratio',
};

// ---------------------------------------------------------------------------
// Formatters & helpers
// ---------------------------------------------------------------------------

function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000)
    return `${+(value / 1_000_000_000).toPrecision(3)}B`;
  if (abs >= 1_000_000) return `${+(value / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000) return `${+(value / 1_000).toPrecision(3)}k`;
  if (abs >= 1) return `${+value.toPrecision(3)}`;
  if (abs === 0) return '0';
  return value.toPrecision(2);
}

function formatLogLabel(value: number): string {
  if (value <= 0) return '';
  const log = Math.log10(value);
  if (Math.abs(log - Math.round(log)) > 0.01) return '';
  return formatCompact(value);
}

const AXIS_LABEL_STYLE = {
  fontSize: 11,
  padding: new Thickness(0, 6, 0, 0),
};
const AXIS_TITLE_STYLE = { fontSize: 12 };

function sortParallelArraysByX<M>(
  xValues: number[],
  yValues: number[],
  metadata: M[]
): { xValues: number[]; yValues: number[]; metadata: M[] } {
  const indices = xValues
    .map((_, i) => i)
    .sort((a, b) => xValues[a] - xValues[b]);
  return {
    xValues: indices.map((i) => xValues[i]),
    yValues: indices.map((i) => yValues[i]),
    metadata: indices.map((i) => metadata[i]),
  };
}

/**
 * Returns the Nth percentile of a numeric array (0–100).
 * Used by the autoZoomY feature to clip the default visible Y range so
 * that tall outliers do not dominate the view.
 * @param {number[]} values - The numeric values to compute the percentile from.
 * @param {number} percentile - The percentile to compute (0–100).
 * @return {number} The value at the given percentile, or 0 if the array is empty.
 */
function computePercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.floor((percentile / 100) * sorted.length))
  );
  return sorted[idx];
}

const DEFAULT_AUTO_ZOOM_Y_PERCENTILE = 95;
const AUTO_ZOOM_Y_PADDING = 1.1;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EnrichedRecord {
  cumulativeOil: number;
  cumulativeGas: number;
  cumulativeWater: number;
  materialBalanceTimeOil: number;
  materialBalanceTimeGas: number;
  materialBalanceTimeWater: number;
}

type EnrichedVolumeRecord = VolumeRecord & EnrichedRecord;
type EnrichedRatioRecord = RatioRecord & EnrichedRecord;

interface MultiWellPointMetadata extends IPointMetadata {
  uwi: string;
  wellName?: string;
  wellQualifier?: string;
  monthsOnline: number;
  originalDate: number;
  cumulativeOil?: number;
  cumulativeWater?: number;
  cumulativeGas?: number;
  materialBalanceTimeOil?: number;
  materialBalanceTimeWater?: number;
  materialBalanceTimeGas?: number;
}

export interface MultiWellProductionData {
  uwi: string;
  name?: string;
  wellQualifier?: string;
  production: {
    volume: VolumeRecord[];
    cumulative: VolumeRecord[];
    ratio: RatioRecord[];
    materialBalanceTime: VolumeRecord[];
  };
}

export interface MultiWellVolumeChartProps {
  /** Chart display type. */
  type: 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';
  /** Chart height in pixels. Defaults to 400. Ignored when `fillHeight`. */
  height?: number;
  /**
   * Grow the canvas to fill the parent instead of using the fixed `height`.
   * The parent must be a flex column with a resolved height. Lets a card in a
   * fixed-height grid row hand its spare space to the chart rather than leaving
   * a gap under it — the charts on the Type Curve pages do this so every chart
   * fills its standard row (INS-11214).
   */
  fillHeight?: boolean;
  /** Production data per well. */
  wells: MultiWellProductionData[];
  /** Optional type curve overlay data. */
  typeCurveData?: {
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null;
  /** Which streams to display (e.g. ['oil'] or ['gor','wor']). */
  selectedSeries: Array<
    MineralType | 'gor' | 'wor' | 'wgr' | 'cgr' | 'watercut'
  >;
  /** Show a loading overlay. */
  isLoading?: boolean;
  /**
   * Unified per-series styling (color, lineWidth, style).
   * All fields are optional; omitted fields fall back to sensible defaults.
   * On hover the matching production series lineWidth is multiplied by 2×.
   */
  seriesStyle?: MultiWellSeriesStyle;
  /**
   * Optional UWI → hex color map for per-well coloring (color-by attribute).
   * Wells whose UWI is not in the map use the default `seriesStyle.production`
   * color. Overlay series (type curve, aggregation, well count) are unaffected.
   */
  uwiColorMap?: Record<string, string>;
  /** When set, the matching UWI's series stays at full opacity and others dim. */
  highlightedUwi?: string | null;
  /** Callback fired when user hovers over a well's series (or null on leave). */
  onWellHover?: (uwi: string | null) => void;
  /** Whether to show type curve lines. Defaults to false. */
  showTypeCurve?: boolean;
  /**
   * Well-count data returned alongside aggregation results.
   * `volumes[i][stream]` = number of wells contributing in month i+1 for that stream.
   * `ratios[i][stream]`  = same, for ratio streams.
   */
  wellCountData?: {
    volumes: TypeCurveVolumeRecord[];
    ratios: TypeCurveRatioRecord[];
  } | null;
  /** Whether to render the well count series. Defaults to false. */
  showWellCount?: boolean;
  /** Optional aggregation overlay data (e.g. P50). Same shape as typeCurveData. */
  aggregationData?: {
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null;
  /** Whether to render the aggregation line. Defaults to false. */
  showAggregation?: boolean;
  /** Human-readable label shown in the aggregation tooltip (e.g. 'P50'). */
  aggregationLabel?: string;
  /** Controlled x-axis type. */
  xAxisType?: XAxisType;
  /** Footer element (e.g. dropdowns for x-axis / chart type switching). */
  footer?: ReactNode;
  /** Header element rendered above the chart. */
  header?: ReactNode;
  /** Y-axis range for logarithmic charts. */
  yLogRange?: { min: number; max: number };
  /** Y-axis range for linear charts. */
  yLinearRange?: { min: number; max?: number };
  /** X-axis range for logarithmic charts. */
  xLogRange?: { min: number; max: number };
  /**
   * Maximum x-axis value for linear charts (default zoom). When combined
   * with `autoZoomX` it acts as a minimum floor — the axis will show at
   * least up to `xLinearMax` and extend further if the data goes beyond.
   */
  xLinearMax?: number;
  /**
   * When true, the linear Y-axis default visible range is clipped to a
   * percentile of the production data instead of the full data extent.
   * This keeps the bulk of the data visible when a few outliers would
   * otherwise force the axis to span e.g. 0–100 while most points sit in 0–10.
   * The user can still pan / zoom out to see the full range.
   * Has no effect on logarithmic Y axes or when `yLinearRange` is provided.
   * Defaults to false.
   */
  autoZoomY?: boolean;
  /**
   * Percentile (0–100) used by `autoZoomY` to cap the Y range. Defaults to 95.
   */
  autoZoomYPercentile?: number;
  /**
   * When true, the linear X-axis default visible range is fit to the full
   * extent of the production data so no points are clipped. Overlay lines
   * (type curve, aggregation, well count) are excluded from the fit so a
   * long type-curve tail can't push the axis past the actual production
   * data. The user can still pan / zoom out to see overlay extents. When
   * combined with `xLinearMax`, that value acts as a minimum floor for the
   * default visible range.
   * Has no effect on logarithmic X axes.
   * Defaults to false.
   */
  autoZoomX?: boolean;
  /**
   * When set to 'daily', Y-values on monthly and logarithmic charts are
   * divided by 30.4 to display average daily rates. Y-axis labels are
   * suffixed with '/day'. Has no effect on cumulative or ratio charts.
   * Defaults to 'monthly' (no scaling).
   */
  volumeDisplayMode?: 'monthly' | 'daily';
  /** Optional forecast overlay data (from bulk_forecasts API). */
  forecastData?: {
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null;
  /** Whether to render the forecast line. Defaults to false. */
  showForecast?: boolean;
  /**
   * When provided, renders an icon-only image-export trigger in the chart
   * header (top-right). Clicking it opens the shared export drawer scoped to
   * this chart's SciChart surface. Omit to render no export trigger (default),
   * so other consumers of this chart are unaffected.
   */
  exportConfig?: { label: string; filenamePrefix: string };
}

// ---------------------------------------------------------------------------
// Custom modifier: WellHoverTooltipModifier
// Hit-tests across well series, fires onWellHover, and renders a tooltip.
// ---------------------------------------------------------------------------

interface HoverModifierConfig {
  tooltipElement: HTMLDivElement | null;
  containerElement: HTMLDivElement | null;
  seriesUwiMap: Map<string, string>;
  seriesStreamMap: Map<string, string>;
  seriesColorMap: Map<string, string>;
  chartType: string;
  onWellHover: ((uwi: string | null) => void) | null;
  /** Fired when the cursor enters/leaves any overlay series (type curve, aggregation, well count). */
  onOverlayHover: ((isHovering: boolean) => void) | null;
  aggregationLabel: string;
  /** Base strokeThickness per overlay series type — used to compute 2× on hover. */
  overlayLineWidths: {
    typeCurve: number;
    aggregation: number;
    wellCount: number;
    forecast: number;
  };
}

class WellHoverTooltipModifier extends ChartModifierBase2D {
  readonly type = 'WellHoverTooltipModifier';
  private config: HoverModifierConfig = {
    tooltipElement: null,
    containerElement: null,
    seriesUwiMap: new Map(),
    seriesStreamMap: new Map(),
    seriesColorMap: new Map(),
    chartType: 'monthly',
    onWellHover: null,
    onOverlayHover: null,
    aggregationLabel: 'Aggregation',
    overlayLineWidths: {
      typeCurve: 2,
      aggregation: 2,
      wellCount: 2,
      forecast: 2,
    },
  };
  private lastHoveredUwi: string | null = null;
  /** Series ID of the overlay line currently being hovered (for 2× thickness). */
  private lastHoveredOverlayId: string | null = null;
  /** Whether an overlay series is currently active — used to avoid redundant callbacks. */
  private isOverlayActive: boolean = false;
  /** Vertical crosshair annotation shown while hovering overlay series. */
  private crosshairAnnotation: VerticalLineAnnotation | null = null;

  setConfig(cfg: HoverModifierConfig) {
    this.config = cfg;
  }

  private getOverlayBaseLineWidth(seriesId: string): number {
    if (seriesId.startsWith('type-curve-'))
      return this.config.overlayLineWidths.typeCurve;
    if (seriesId.startsWith('aggregation-'))
      return this.config.overlayLineWidths.aggregation;
    if (seriesId.startsWith('well-count-'))
      return this.config.overlayLineWidths.wellCount;
    if (seriesId.startsWith('forecast-'))
      return this.config.overlayLineWidths.forecast;
    return 2;
  }

  /**
   * Looks up the well count value for a given stream at a specific month
   * by reading the `well-count-{stream}` series that already lives on the chart.
   * @param {string} stream - The stream identifier (e.g. 'oil', 'gas').
   * @param {number} month - The monthsOnline value to look up.
   * @return {number|null} The well count at the given month, or null if not found.
   */
  private lookupWellCountAtMonth(stream: string, month: number): number | null {
    const surface = this.parentSurface;
    if (!surface) return null;
    const wcSeries = surface.renderableSeries
      .asArray()
      .find((s) => s.id === `well-count-${stream}`);
    if (!wcSeries?.isVisible) return null;
    const ds = wcSeries.dataSeries as XyDataSeries;
    const count = ds.count();
    for (let i = 0; i < count; i++) {
      const meta = ds.getMetadataAt(i) as MultiWellPointMetadata;
      if (meta?.monthsOnline === month) {
        return ds.getNativeYValues().get(i);
      }
    }
    return null;
  }

  /**
   * Looks up the type curve value for a given stream at a specific month.
   * @param {string} stream - The stream identifier (e.g. 'oil', 'gas').
   * @param {number} month - The monthsOnline value to look up.
   * @return {number|null} The type curve value at the given month, or null if not found.
   */
  private lookupTypeCurveAtMonth(stream: string, month: number): number | null {
    const surface = this.parentSurface;
    if (!surface) return null;
    const tcSeries = surface.renderableSeries
      .asArray()
      .find((s) => s.id === `type-curve-${stream}`);
    if (!tcSeries?.isVisible) return null;
    const ds = tcSeries.dataSeries as XyDataSeries;
    const count = ds.count();
    for (let i = 0; i < count; i++) {
      const meta = ds.getMetadataAt(i) as MultiWellPointMetadata;
      if (meta?.monthsOnline === month) {
        return ds.getNativeYValues().get(i);
      }
    }
    return null;
  }

  /**
   * Looks up the aggregation value for a given stream at a specific month.
   * @param {string} stream - The stream identifier (e.g. 'oil', 'gas').
   * @param {number} month - The monthsOnline value to look up.
   * @return {number|null} The aggregation value at the given month, or null if not found.
   */
  private lookupAggregationAtMonth(
    stream: string,
    month: number
  ): number | null {
    const surface = this.parentSurface;
    if (!surface) return null;
    const aggSeries = surface.renderableSeries
      .asArray()
      .find((s) => s.id === `aggregation-${stream}`);
    if (!aggSeries?.isVisible) return null;
    const ds = aggSeries.dataSeries as XyDataSeries;
    const count = ds.count();
    for (let i = 0; i < count; i++) {
      const meta = ds.getMetadataAt(i) as MultiWellPointMetadata;
      if (meta?.monthsOnline === month) {
        return ds.getNativeYValues().get(i);
      }
    }
    return null;
  }

  /**
   * Returns the crosshair annotation, creating it lazily on first call.
   * @return {VerticalLineAnnotation|null} The annotation instance, or null if no surface is available.
   */
  private getCrosshair(): VerticalLineAnnotation | null {
    const surface = this.parentSurface;
    if (!surface) return null;
    if (!this.crosshairAnnotation) {
      this.crosshairAnnotation = new VerticalLineAnnotation({
        x1: 0,
        stroke: '#888888',
        strokeThickness: 1,
        strokeDashArray: [4, 4],
        isHidden: true,
        xAxisId: DEFAULT_X_AXIS_ID,
      });
      surface.annotations.add(this.crosshairAnnotation);
    }
    return this.crosshairAnnotation;
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    const surface = this.parentSurface;
    if (!surface || !this.config.tooltipElement) return;

    let bestUwi: string | null = null;
    let bestSeriesId: string | null = null;
    let bestDist = Infinity;
    let bestMeta: MultiWellPointMetadata | null = null;
    let bestStream = '';
    let bestYValue = 0;
    let bestXValue = 0;

    const OVERLAY_UWIS = new Set([
      '__type_curve__',
      '__aggregation__',
      '__well_count__',
      '__forecast__',
    ]);

    // Two-pass hit-test: overlay series (aggregation, type curve, well count)
    // always win over production wells so that hovering an overlay line never
    // accidentally highlights a production well that happens to be nearby.
    // Pass 1 — overlay series only.
    for (const series of surface.renderableSeries.asArray()) {
      if (!series.isVisible) continue;
      const id = series.id ?? '';
      const uwi = this.config.seriesUwiMap.get(id);
      if (!uwi || !OVERLAY_UWIS.has(uwi)) continue;

      const hit = series.hitTestProvider.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        HIT_TEST_RADIUS
      );

      if (hit.isHit && hit.dataSeriesIndex >= 0) {
        const dx = args.mousePoint.x - hit.hitTestPointValues.x;
        const dy = args.mousePoint.y - hit.hitTestPointValues.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < bestDist) {
          bestDist = dist;
          bestUwi = uwi;
          bestSeriesId = id;
          bestStream = this.config.seriesStreamMap.get(id) ?? '';
          bestYValue = hit.yValue;
          bestXValue = hit.xValue;
          const ds = series.dataSeries as XyDataSeries;
          bestMeta = ds.getMetadataAt(
            hit.dataSeriesIndex
          ) as MultiWellPointMetadata;
        }
      }
    }

    // Pass 2 — production wells, only if no overlay was already found.
    if (!bestUwi) {
      for (const series of surface.renderableSeries.asArray()) {
        if (!series.isVisible) continue;
        const id = series.id ?? '';
        const uwi = this.config.seriesUwiMap.get(id);
        if (!uwi || OVERLAY_UWIS.has(uwi)) continue;

        const hit = series.hitTestProvider.hitTest(
          args.mousePoint.x,
          args.mousePoint.y,
          HIT_TEST_RADIUS
        );

        if (hit.isHit && hit.dataSeriesIndex >= 0) {
          const dx = args.mousePoint.x - hit.hitTestPointValues.x;
          const dy = args.mousePoint.y - hit.hitTestPointValues.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < bestDist) {
            bestDist = dist;
            bestUwi = uwi;
            bestSeriesId = id;
            bestStream = this.config.seriesStreamMap.get(id) ?? '';
            bestYValue = hit.yValue;
            bestXValue = hit.xValue;
            const ds = series.dataSeries as XyDataSeries;
            bestMeta = ds.getMetadataAt(
              hit.dataSeriesIndex
            ) as MultiWellPointMetadata;
          }
        }
      }
    }

    const isTypeCurve = bestUwi === '__type_curve__';
    const isAggregation = bestUwi === '__aggregation__';
    const isWellCount = bestUwi === '__well_count__';
    const isForecast = bestUwi === '__forecast__';
    const isOverlay = isTypeCurve || isAggregation || isWellCount || isForecast;

    // Manage overlay series hover thickness (2× when directly hovered, no color change).
    // Restore the previous overlay series before applying to the new one.
    if (
      this.lastHoveredOverlayId &&
      this.lastHoveredOverlayId !== bestSeriesId
    ) {
      const prev = surface.renderableSeries
        .asArray()
        .find((s) => s.id === this.lastHoveredOverlayId);
      if (prev) {
        (prev as FastLineRenderableSeries).strokeThickness =
          this.getOverlayBaseLineWidth(this.lastHoveredOverlayId);
      }
      this.lastHoveredOverlayId = null;
    }
    if (isOverlay && bestSeriesId) {
      const overlaySeries = surface.renderableSeries
        .asArray()
        .find((s) => s.id === bestSeriesId);
      if (overlaySeries) {
        (overlaySeries as FastLineRenderableSeries).strokeThickness =
          this.getOverlayBaseLineWidth(bestSeriesId) +
          HOVER_STROKE_THICKNESS_DELTA;
      }
      this.lastHoveredOverlayId = bestSeriesId;
      // Show vertical crosshair at hovered x data coordinate
      const crosshair = this.getCrosshair();
      if (crosshair) {
        crosshair.x1 = bestXValue;
        crosshair.isHidden = false;
      }
    } else {
      // Hide crosshair when not on an overlay series
      if (this.crosshairAnnotation) this.crosshairAnnotation.isHidden = true;
    }

    // Fire onOverlayHover only when the active state actually changes
    const overlayNowActive = this.lastHoveredOverlayId !== null;
    if (overlayNowActive !== this.isOverlayActive) {
      this.isOverlayActive = overlayNowActive;
      this.config.onOverlayHover?.(overlayNowActive);
    }

    // onWellHover is only for production wells (real UWIs).
    // Overlay series never trigger it. Empty-space clears the highlight
    // ONLY when the cursor is not currently sitting on an overlay series
    // (guards against hit-test misses that occur between frames during
    // fast movement over type-curve / aggregation / well-count lines).
    if (!isOverlay && bestUwi !== null && bestUwi !== this.lastHoveredUwi) {
      this.lastHoveredUwi = bestUwi;
      this.config.onWellHover?.(bestUwi);
    } else if (
      bestUwi === null &&
      this.lastHoveredUwi !== null &&
      this.lastHoveredOverlayId === null
    ) {
      this.lastHoveredUwi = null;
      this.config.onWellHover?.(null);
    }

    // Render tooltip
    const { tooltipElement, containerElement } = this.config;
    if (!bestUwi || !bestMeta) {
      tooltipElement.style.display = 'none';
      return;
    }

    const isRatio = this.config.chartType === 'ratio';
    const decimals = isRatio ? 2 : 0;
    const formatted = formatNumber({
      value: bestYValue,
      minPrecision: decimals,
    });
    const yLabel = STREAM_Y_AXIS_TITLES[bestStream] ?? bestStream;

    let lines: string[];
    if (isTypeCurve || isAggregation || isForecast) {
      const label = isForecast
        ? 'Forecast'
        : isTypeCurve
          ? 'Type Curve'
          : this.config.aggregationLabel;
      const wc = this.lookupWellCountAtMonth(bestStream, bestMeta.monthsOnline);
      lines = [
        `<strong>${label}</strong>`,
        `Months Online: ${bestMeta.monthsOnline}`,
        ...(wc !== null
          ? [
              `Well Count: ${formatNumber({
                value: Math.round(wc),
                minPrecision: 0,
              })}`,
            ]
          : []),
        `${yLabel}: ${formatted}`,
      ];
    } else if (isWellCount) {
      lines = [
        `<strong>Well Count</strong>`,
        `Months Online: ${bestMeta.monthsOnline}`,
        `Count: ${Math.round(bestYValue)}`,
      ];
    } else {
      // Production well tooltip with context lines
      const wellName = bestMeta.wellName || '';
      lines = [];
      if (wellName) lines.push(`Well Name: ${wellName}`);
      lines.push(`UWI: ${bestUwi}`);
      if (bestMeta.wellQualifier)
        lines.push(`Well Qualifier: ${bestMeta.wellQualifier}`);
      lines.push(`Months Online: ${bestMeta.monthsOnline}`);
      lines.push(`${yLabel}: ${formatted}`);

      // Add context lines: Type Curve, Aggregation, Well Count
      const tcVal = this.lookupTypeCurveAtMonth(
        bestStream,
        bestMeta.monthsOnline
      );
      const aggVal = this.lookupAggregationAtMonth(
        bestStream,
        bestMeta.monthsOnline
      );
      const wcVal = this.lookupWellCountAtMonth(
        bestStream,
        bestMeta.monthsOnline
      );

      if (tcVal !== null) {
        const tcFormatted = formatNumber({
          value: tcVal,
          minPrecision: decimals,
        });
        lines.push(`Type Curve: ${tcFormatted}`);
      }
      if (aggVal !== null) {
        const aggFormatted = formatNumber({
          value: aggVal,
          minPrecision: decimals,
        });
        lines.push(`Type Well: ${aggFormatted}`);
      }
      if (wcVal !== null) {
        lines.push(
          `Well Count: ${formatNumber({
            value: Math.round(wcVal),
            minPrecision: 0,
          })}`
        );
      }
    }

    tooltipElement.innerHTML = `<div style="background:rgba(255,255,255,0.95);border:1px solid #ccc;padding:8px;border-radius:4px;font-size:12px;pointer-events:none;white-space:nowrap;line-height:1.6;">
      ${lines.join('<br/>')}
    </div>`;
    tooltipElement.style.display = 'block';

    if (containerElement) {
      const rect = containerElement.getBoundingClientRect();
      const e = args.nativeEvent as MouseEvent;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cW = rect.width;
      const cH = rect.height;
      const tW = tooltipElement.offsetWidth;
      const tH = tooltipElement.offsetHeight;

      let left = mx + 15;
      let top = my - tH - 8;

      if (top < 0) top = my + 12;
      if (left + tW > cW) left = mx - tW - 15;

      tooltipElement.style.left = `${Math.max(0, left)}px`;
      tooltipElement.style.top = `${Math.max(0, Math.min(top, cH - tH))}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.config.tooltipElement) {
      this.config.tooltipElement.style.display = 'none';
    }
    if (this.lastHoveredUwi !== null) {
      this.lastHoveredUwi = null;
      this.config.onWellHover?.(null);
    }
    // Hide crosshair when cursor leaves the chart
    if (this.crosshairAnnotation) this.crosshairAnnotation.isHidden = true;
    if (this.isOverlayActive) {
      this.isOverlayActive = false;
      this.config.onOverlayHover?.(false);
    }
    // Restore overlay series thickness if the cursor leaves the chart
    if (this.lastHoveredOverlayId) {
      const surface = this.parentSurface;
      if (surface) {
        const prev = surface.renderableSeries
          .asArray()
          .find((s) => s.id === this.lastHoveredOverlayId);
        if (prev) {
          (prev as FastLineRenderableSeries).strokeThickness =
            this.getOverlayBaseLineWidth(this.lastHoveredOverlayId);
        }
      }
      this.lastHoveredOverlayId = null;
    }
  }
}

// ---------------------------------------------------------------------------
// MultiWellVolumeChart Component
// ---------------------------------------------------------------------------

export const MultiWellVolumeChart = ({
  type,
  height = 400,
  fillHeight = false,
  wells,
  typeCurveData,
  wellCountData,
  showWellCount = false,
  aggregationData,
  showAggregation = false,
  aggregationLabel,
  selectedSeries,
  isLoading,
  seriesStyle,
  uwiColorMap,
  highlightedUwi,
  onWellHover,
  showTypeCurve = false,
  xAxisType: controlledXAxisType,
  footer,
  header,
  yLogRange,
  yLinearRange,
  xLogRange,
  xLinearMax,
  autoZoomY = false,
  autoZoomYPercentile = DEFAULT_AUTO_ZOOM_Y_PERCENTILE,
  autoZoomX = false,
  volumeDisplayMode = 'monthly',
  forecastData,
  showForecast = false,
  exportConfig,
}: MultiWellVolumeChartProps) => {
  // Whether to apply daily scaling (÷30.4) to Y-values. Only applies to
  // volume charts (monthly, logarithmic) — not cumulative or ratio.
  const applyDailyScaling =
    volumeDisplayMode === 'daily' &&
    (type === 'monthly' || type === 'logarithmic');

  /** Conditionally scale a Y-value from monthly to daily. */
  const scaleY = useCallback(
    (y: number): number => (applyDailyScaling ? monthlyVolumeToDaily(y) : y),
    [applyDailyScaling]
  );

  // Resolve each series style once — falls back to DEFAULT_SERIES_STYLE per key.
  const productionStyle = resolveSeriesStyle(
    'production',
    seriesStyle?.production
  );
  const typeCurveStyle = resolveSeriesStyle(
    'typeCurve',
    seriesStyle?.typeCurve
  );
  const wellCountStyle = resolveSeriesStyle(
    'wellCount',
    seriesStyle?.wellCount
  );
  const aggregationStyle = resolveSeriesStyle(
    'aggregation',
    seriesStyle?.aggregation
  );
  const forecastStyle = resolveSeriesStyle('forecast', seriesStyle?.forecast);
  const [internalXAxisType] = useState<XAxisType>(
    type === 'logarithmic' ? 'count' : 'date'
  );
  const xAxisType = controlledXAxisType ?? internalXAxisType;
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOverlayHovered, setIsOverlayHovered] = useState(false);
  const decimalsForChart = type === 'ratio' ? 2 : 0;

  const surfaceRef = useRef<SciChartSurface | null>(null);
  const wasmContextRef = useRef<TSciChart | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Scale axis-label/title fonts and series stroke/marker weight on export so
  // the downloaded image keeps the on-screen proportions (see helper).
  const exportElementHooks = useMemo(
    () =>
      createSciChartElementHooks(() => surfaceRef.current, {
        // Show the x-axis title in the export only (it's intentionally omitted
        // from the live chart, which has the x-axis switcher in the footer).
        getExportAxisTitles: () => [
          { axisId: DEFAULT_X_AXIS_ID, title: X_AXIS_TYPE_LABELS[xAxisType] },
        ],
      }),
    [xAxisType]
  );
  // Separate ref for the canvas-only area so tooltip coordinates (which are
  // relative to the SciChart canvas) are not thrown off by the header height.
  const chartCanvasRef = useRef<HTMLDivElement>(null);
  const chartDivId = useRef(
    `mw-volume-chart-${Math.random().toString(36).slice(2, 11)}`
  ).current;
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const logYRangeRef = useRef<NumberRange | null>(null);
  // Per-axis Y range computed when `autoZoomY` is on. Populated during the
  // rebuild and reused by the double-click reset handler so that resetting
  // returns the user to the smart-zoomed view, not full extents.
  const autoZoomYRangesRef = useRef<Map<string, NumberRange>>(new Map());
  // X range computed when `autoZoomX` is on. Reused by the double-click reset
  // handler so resetting returns the user to the smart-zoomed view.
  const autoZoomXRangeRef = useRef<NumberRange | null>(null);

  // Map series ID → UWI for the highlight effect
  const seriesUwiMapRef = useRef<Map<string, string>>(new Map());
  // Series ID → stream / color — promoted to refs so the decoupled forecast
  // render can update the forecast entries in place (the hover tooltip reads
  // these same map objects) without a full chart rebuild.
  const seriesStreamMapRef = useRef<Map<string, string>>(new Map());
  const seriesColorMapRef = useRef<Map<string, string>>(new Map());

  // -------------------------------------------------------------------
  // Data enrichment per well
  // -------------------------------------------------------------------

  const enrichVolumeRecord = useCallback(
    (
      baseArray: VolumeRecord[] | RatioRecord[],
      cumulativeArray: VolumeRecord[],
      mbtArray: VolumeRecord[]
    ) => {
      // Index the join arrays by date once (O(n)) instead of a `.find` per
      // record (O(n²)); `has` guard keeps first-match semantics like `.find`.
      const cumByDate = new Map<string, VolumeRecord>();
      for (const r of cumulativeArray) {
        if (!cumByDate.has(r.date)) cumByDate.set(r.date, r);
      }
      const mbtByDate = new Map<string, VolumeRecord>();
      for (const r of mbtArray) {
        if (!mbtByDate.has(r.date)) mbtByDate.set(r.date, r);
      }
      return baseArray.map((baseRecord) => {
        const matchingCumulative = cumByDate.get(baseRecord.date);
        const matchingMBT = mbtByDate.get(baseRecord.date);
        return {
          ...baseRecord,
          cumulativeOil: matchingCumulative?.oil,
          cumulativeWater: matchingCumulative?.water,
          cumulativeGas: matchingCumulative?.gas,
          materialBalanceTimeOil: matchingMBT?.oil,
          materialBalanceTimeWater: matchingMBT?.water,
          materialBalanceTimeGas: matchingMBT?.gas,
        };
      });
    },
    []
  );

  const enrichedWells = useMemo(
    () =>
      wells.map((well) => ({
        uwi: well.uwi,
        name: well.name,
        wellQualifier: well.wellQualifier,
        production: {
          volume: enrichVolumeRecord(
            well.production.volume,
            well.production.cumulative,
            well.production.materialBalanceTime
          ) as EnrichedVolumeRecord[],
          cumulative: enrichVolumeRecord(
            well.production.cumulative,
            well.production.cumulative,
            well.production.materialBalanceTime
          ) as EnrichedVolumeRecord[],
          ratio: enrichVolumeRecord(
            well.production.ratio,
            well.production.cumulative,
            well.production.materialBalanceTime
          ) as EnrichedRatioRecord[],
          materialBalanceTime: enrichVolumeRecord(
            well.production.materialBalanceTime,
            well.production.cumulative,
            well.production.materialBalanceTime
          ) as EnrichedVolumeRecord[],
        },
      })),
    [wells, enrichVolumeRecord]
  );

  // -------------------------------------------------------------------
  // Earliest dates
  // -------------------------------------------------------------------

  const earliestDate = useMemo(() => {
    const allDates = enrichedWells
      .flatMap((well) => well.production[volumeMap[type]].map((r) => r.date))
      .filter(Boolean);
    if (allDates.length === 0) return moment.utc();
    const minVal = allDates.reduce(
      (min, d) => Math.min(min, moment.utc(d).valueOf()),
      Infinity
    );
    return moment.utc(minVal);
  }, [enrichedWells, type]);

  const earliestProductionDate = useMemo(() => {
    const allDates = enrichedWells
      .flatMap((well) => well.production[volumeMap[type]].map((r) => r.date))
      .filter(Boolean);
    if (allDates.length === 0) return moment.utc();
    const minVal = allDates.reduce(
      (min, d) => Math.min(min, moment.utc(d).valueOf()),
      Infinity
    );
    return moment.utc(minVal);
  }, [enrichedWells, type]);

  // -------------------------------------------------------------------
  // Type curve enrichment
  // -------------------------------------------------------------------

  const enrichedTypeCurveData = useMemo(() => {
    if (!typeCurveData || typeCurveData.volume.length === 0) return [];
    const {
      volume: volumesData,
      cumulative: cumulativesData,
      ratio: ratiosData,
      materialBalanceTime: mbtsData,
    } = typeCurveData;

    return volumesData.map(
      (volumeRecord: TypeCurveVolumeRecord, index: number) => {
        const date = moment
          .utc(earliestProductionDate)
          .add(volumeRecord.month - 1, 'months');
        const cumulativeRecord = cumulativesData[index];
        const ratioRecord = ratiosData[index];
        const mbtRecord = mbtsData[index];
        return {
          date: date.toDate(),
          oil: volumeRecord.oil ?? 0,
          gas: volumeRecord.gas ?? 0,
          water: volumeRecord.water ?? 0,
          cumulativeOil: cumulativeRecord?.oil ?? 0,
          cumulativeGas: cumulativeRecord?.gas ?? 0,
          cumulativeWater: cumulativeRecord?.water ?? 0,
          materialBalanceTimeOil: mbtRecord?.oil ?? 0,
          materialBalanceTimeGas: mbtRecord?.gas ?? 0,
          materialBalanceTimeWater: mbtRecord?.water ?? 0,
          gor: ratioRecord?.gor ?? 0,
          wor: ratioRecord?.wor ?? 0,
          wgr: ratioRecord?.wgr ?? 0,
          cgr: ratioRecord?.cgr ?? 0,
          watercut: ratioRecord?.watercut ?? 0,
        };
      }
    );
  }, [typeCurveData, earliestProductionDate]);

  // -------------------------------------------------------------------
  // Aggregation data enrichment (same shape as type curve enrichment)
  // -------------------------------------------------------------------

  const enrichedAggregationData = useMemo(() => {
    if (!aggregationData || aggregationData.volume.length === 0) return [];
    const {
      volume: volumesData,
      cumulative: cumulativesData,
      ratio: ratiosData,
      materialBalanceTime: mbtsData,
    } = aggregationData;

    return volumesData.map(
      (volumeRecord: TypeCurveVolumeRecord, index: number) => {
        const date = moment
          .utc(earliestProductionDate)
          .add(volumeRecord.month - 1, 'months');
        const cumulativeRecord = cumulativesData[index];
        const ratioRecord = ratiosData[index];
        const mbtRecord = mbtsData[index];
        return {
          date: date.toDate(),
          oil: volumeRecord.oil ?? 0,
          gas: volumeRecord.gas ?? 0,
          water: volumeRecord.water ?? 0,
          cumulativeOil: cumulativeRecord?.oil ?? 0,
          cumulativeGas: cumulativeRecord?.gas ?? 0,
          cumulativeWater: cumulativeRecord?.water ?? 0,
          materialBalanceTimeOil: mbtRecord?.oil ?? 0,
          materialBalanceTimeGas: mbtRecord?.gas ?? 0,
          materialBalanceTimeWater: mbtRecord?.water ?? 0,
          gor: ratioRecord?.gor ?? 0,
          wor: ratioRecord?.wor ?? 0,
          wgr: ratioRecord?.wgr ?? 0,
          cgr: ratioRecord?.cgr ?? 0,
          watercut: ratioRecord?.watercut ?? 0,
        };
      }
    );
  }, [aggregationData, earliestProductionDate]);

  // -------------------------------------------------------------------
  // Forecast data enrichment (same shape as aggregation enrichment)
  // -------------------------------------------------------------------

  const enrichedForecastData = useMemo(() => {
    if (!forecastData || forecastData.volume.length === 0) return [];
    const {
      volume: volumesData,
      cumulative: cumulativesData,
      ratio: ratiosData,
      materialBalanceTime: mbtsData,
    } = forecastData;

    return volumesData.map(
      (volumeRecord: TypeCurveVolumeRecord, index: number) => {
        const date = moment
          .utc(earliestProductionDate)
          .add(volumeRecord.month - 1, 'months');
        const cumulativeRecord = cumulativesData[index];
        const ratioRecord = ratiosData[index];
        const mbtRecord = mbtsData[index];
        return {
          date: date.toDate(),
          oil: volumeRecord.oil ?? 0,
          gas: volumeRecord.gas ?? 0,
          water: volumeRecord.water ?? 0,
          cumulativeOil: cumulativeRecord?.oil ?? 0,
          cumulativeGas: cumulativeRecord?.gas ?? 0,
          cumulativeWater: cumulativeRecord?.water ?? 0,
          materialBalanceTimeOil: mbtRecord?.oil ?? 0,
          materialBalanceTimeGas: mbtRecord?.gas ?? 0,
          materialBalanceTimeWater: mbtRecord?.water ?? 0,
          gor: ratioRecord?.gor ?? 0,
          wor: ratioRecord?.wor ?? 0,
          wgr: ratioRecord?.wgr ?? 0,
          cgr: ratioRecord?.cgr ?? 0,
          watercut: ratioRecord?.watercut ?? 0,
        };
      }
    );
  }, [forecastData, earliestProductionDate]);

  // -------------------------------------------------------------------
  // X-value calculation
  // -------------------------------------------------------------------

  const getXValue = useCallback(
    (
      record: EnrichedVolumeRecord | EnrichedRatioRecord,
      axisType: XAxisType,
      refDate: moment.Moment
    ): number => {
      if (axisType === 'date') return moment.utc(record[axisType]).valueOf();
      if (axisType === 'count')
        return moment.utc(record.date).diff(refDate, 'months') + 1;
      return record[axisType];
    },
    []
  );

  // -------------------------------------------------------------------
  // Forecast overlay render — DECOUPLED from the heavy per-well rebuild.
  //
  // The forecast series (3–5 small series) are (re)built here and called BOTH
  // at the end of the main rebuild (so they re-attach after a full clear) and
  // from a dedicated effect on forecast changes. This keeps a forecast response
  // from re-running the expensive per-well series construction. The forecast
  // entries are written into the shared lookup-map refs the hover tooltip reads.
  // -------------------------------------------------------------------
  const renderForecast = useCallback(() => {
    const surface = surfaceRef.current;
    const wasm = wasmContextRef.current;
    if (!surface || !wasm) return;
    // Axes must exist (built by the main rebuild) before attaching series.
    if (!surface.yAxes.getById(PRIMARY_Y_AXIS_ID)) return;

    surface.suspendUpdates();

    // Tear down any existing forecast series + their lookup-map entries.
    surface.renderableSeries
      .asArray()
      .filter((rs) => rs.id?.startsWith('forecast-'))
      .forEach((rs) => surface.renderableSeries.remove(rs));
    for (const id of [...seriesUwiMapRef.current.keys()]) {
      if (id.startsWith('forecast-')) {
        seriesUwiMapRef.current.delete(id);
        seriesStreamMapRef.current.delete(id);
        seriesColorMapRef.current.delete(id);
      }
    }

    if (showForecast && enrichedForecastData.length > 0) {
      const singleStreamRatio = type === 'ratio' && selectedSeries.length === 1;
      const forecastStreams =
        type === 'ratio'
          ? ['gor', 'wor', 'wgr', 'cgr', 'watercut']
          : ['oil', 'gas', 'water'];

      for (const baseStream of forecastStreams) {
        const xValues: number[] = [];
        const yValues: number[] = [];
        const metadata: MultiWellPointMetadata[] = [];

        for (const record of enrichedForecastData) {
          let yVal: number;
          if (type === 'ratio') {
            yVal = record[baseStream as keyof typeof record] as number;
          } else if (type === 'cumulative') {
            const key = `cumulative${baseStream
              .charAt(0)
              .toUpperCase()}${baseStream.slice(1)}` as keyof typeof record;
            yVal = record[key] as number;
          } else {
            yVal = record[baseStream as keyof typeof record] as number;
          }

          if (!yVal || yVal <= 0) continue;

          const xVal = getXValue(
            {
              date: moment.utc(record.date).format('YYYY-MM-DD'),
              oil: record.oil,
              gas: record.gas,
              water: record.water,
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            } as EnrichedVolumeRecord,
            xAxisType,
            earliestProductionDate
          );

          if (xVal === null) continue;

          xValues.push(xVal);
          yValues.push(scaleY(yVal));
          metadata.push({
            isSelected: false,
            uwi: '__forecast__',
            monthsOnline: xValues.length,
            originalDate: moment.utc(record.date).valueOf(),
          });
        }

        if (xValues.length === 0) continue;

        const sortedFc = sortParallelArraysByX(xValues, yValues, metadata);

        const yAxisId =
          !singleStreamRatio && (baseStream === 'wgr' || baseStream === 'cgr')
            ? SECONDARY_Y_AXIS_ID
            : PRIMARY_Y_AXIS_ID;

        const fcSeriesId = `forecast-${baseStream}`;

        const dataSeries = new XyDataSeries(wasm, {
          xValues: sortedFc.xValues,
          yValues: sortedFc.yValues,
          dataSeriesName: `Forecast ${baseStream}`,
          metadata: sortedFc.metadata,
        });

        const lineSeries = new FastLineRenderableSeries(wasm, {
          id: fcSeriesId,
          dataSeries,
          stroke: forecastStyle.color,
          strokeThickness: forecastStyle.lineWidth,
          strokeDashArray: getDashArray(forecastStyle.style),
          opacity: 1,
          yAxisId,
          isVisible:
            showForecast &&
            selectedSeries.some(
              (s) => s.toLowerCase() === baseStream.toLowerCase()
            ),
        });
        surface.renderableSeries.add(lineSeries);

        seriesUwiMapRef.current.set(fcSeriesId, '__forecast__');
        seriesStreamMapRef.current.set(fcSeriesId, baseStream);
        seriesColorMapRef.current.set(fcSeriesId, forecastStyle.color);
      }
    }

    surface.resumeUpdates();
  }, [
    showForecast,
    enrichedForecastData,
    type,
    xAxisType,
    earliestProductionDate,
    scaleY,
    selectedSeries,
    forecastStyle,
    getXValue,
  ]);

  // -------------------------------------------------------------------
  // SciChart surface initialization
  // -------------------------------------------------------------------

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        ensureWasmConfigured();

        const theme = new SciChartJSLightTheme();
        theme.sciChartBackground = '#ffffff';
        theme.majorGridLineBrush = '#e0e0e0';
        theme.minorGridLineBrush = '#f0f0f0';

        const { sciChartSurface, wasmContext } = await SciChartSurface.create(
          chartDivId,
          { theme, padding: new Thickness(10, 20, 10, 20) }
        );

        if (!isMounted) {
          sciChartSurface.delete();
          return;
        }
        surfaceRef.current = sciChartSurface;
        wasmContextRef.current = wasmContext;
        setIsInitialized(true);
      } catch (err) {
        console.error(
          'MultiWellVolumeChart: SciChart initialization failed:',
          err
        );
      }
    };

    init();

    return () => {
      isMounted = false;
      surfaceRef.current?.delete();
      surfaceRef.current = null;
      wasmContextRef.current = null;
    };
  }, [chartDivId]);

  // -------------------------------------------------------------------
  // Rebuild chart when data or settings change
  // -------------------------------------------------------------------

  useEffect(() => {
    if (!isInitialized || !surfaceRef.current || !wasmContextRef.current)
      return;

    const cancelRebuild = scheduleChartRebuild(chartDivId, () => {
      const surface = surfaceRef.current;
      const wasm = wasmContextRef.current;
      if (!surface || !wasm) return;

      surface.suspendUpdates();
      surface.renderableSeries.clear();
      surface.xAxes.clear();
      surface.yAxes.clear();
      surface.annotations.clear();
      surface.chartModifiers.clear();

      const newSeriesUwiMap = new Map<string, string>();
      const seriesStreamMap = new Map<string, string>();
      const seriesColorMap = new Map<string, string>();

      // =================================================================
      // 1. Build axes
      // =================================================================

      const isLogXAxis = type === 'logarithmic' && xAxisType !== 'date';
      const isDateXAxis = xAxisType === 'date';

      // Shared x-axis styling: subtle black baseline + visible major ticks,
      // no minor ticks — mirrors the SciChartVolumes look.
      const xAxisStyle = {
        axisBorder: { borderTop: 1, color: '#161616' },
        majorTickLineStyle: {
          tickSize: 5,
          color: '#161616',
          strokeThickness: 1,
        },
        drawMinorTickLines: false,
      };

      if (isLogXAxis) {
        const logXLabelProvider = new NumericLabelProvider();
        logXLabelProvider.formatLabel = formatLogLabel;
        surface.xAxes.add(
          new LogarithmicAxis(wasm, {
            id: DEFAULT_X_AXIS_ID,
            axisAlignment: EAxisAlignment.Bottom,
            autoRange: EAutoRange.Once,
            drawMajorGridLines: true,
            drawMinorGridLines: false,
            labelProvider: logXLabelProvider,
            labelStyle: AXIS_LABEL_STYLE,
            ...xAxisStyle,
          })
        );
      } else {
        const xAxisOptions: ConstructorParameters<typeof NumericAxis>[1] = {
          id: DEFAULT_X_AXIS_ID,
          axisAlignment: EAxisAlignment.Bottom,
          autoRange: EAutoRange.Once,
          drawMajorGridLines: true,
          drawMinorGridLines: false,
          labelStyle: AXIS_LABEL_STYLE,
          ...xAxisStyle,
        };

        if (isDateXAxis) {
          const dateLabelProvider = new NumericLabelProvider();
          dateLabelProvider.formatLabel = (dataValue: number) =>
            moment.utc(dataValue).format('MMM YYYY');
          dateLabelProvider.formatCursorLabel = (dataValue: number) =>
            moment.utc(dataValue).format('MMMM YYYY');
          xAxisOptions.labelProvider = dateLabelProvider;
        } else {
          xAxisOptions.labelPrecision = 0;
        }

        const xAxis = new NumericAxis(wasm, xAxisOptions);
        if (isDateXAxis) {
          xAxis.tickProvider = new MonthlyTickProvider(wasm);
        }
        surface.xAxes.add(xAxis);
      }

      const isLogYAxis = type === 'monthly' || type === 'logarithmic';
      const isVolumeChart = type !== 'ratio';
      const yGrowBy = new NumberRange(0.05, 0.1);

      // Pre-compute log Y range from monthly volume data. Used for:
      //   - the primary log Y axis on monthly + logarithmic charts
      //   - the right-side well-count log axis on ALL volume charts
      //     (monthly, logarithmic, cumulative) so the well-count axis is
      //     consistent across chart types and matches the monthly look.
      // Always reads `well.production.volume` so the range is identical
      // regardless of whether the chart is showing monthly, log, or
      // cumulative data.
      let yLogMin = 1;
      let yLogMax = 1e6;
      if (isVolumeChart) {
        const allYVals: number[] = [];
        for (const well of enrichedWells) {
          const data = well.production.volume;
          for (const stream of ['oil', 'gas', 'water'] as const) {
            for (const record of data) {
              const yVal = record[stream as keyof typeof record] as number;
              if (typeof yVal === 'number' && yVal > 0) allYVals.push(yVal);
            }
          }
        }
        if (allYVals.length > 0) {
          yLogMin = Math.pow(
            10,
            Math.floor(
              Math.log10(
                allYVals.reduce((min, v) => Math.min(min, v), Infinity)
              )
            )
          );
          yLogMax = Math.pow(
            10,
            Math.ceil(
              Math.log10(
                allYVals.reduce((max, v) => Math.max(max, v), -Infinity)
              )
            )
          );
        }
        // The log y-axis (and the well-count secondary axis that mirrors
        // it on volume charts) must always start at 1 — never drop below.
        // Guarantee a 3-decade visible range by extending the upper bound
        // upward instead of pulling the lower bound below 1.
        yLogMin = Math.max(1, yLogMin);
        if (yLogMax <= yLogMin) yLogMax = yLogMin * 10;
        while (Math.log10(yLogMax / yLogMin) < 3) yLogMax *= 10;
        logYRangeRef.current = isLogYAxis
          ? new NumberRange(yLogMin, yLogMax)
          : null;
      } else {
        logYRangeRef.current = null;
      }

      const makeLabelProvider = (formatter: (v: number) => string) => {
        const lp = new NumericLabelProvider();
        lp.formatLabel = formatter;
        return lp;
      };

      const yAxisBase = {
        axisAlignment: EAxisAlignment.Left,
        autoRange: EAutoRange.Once,
        drawMajorGridLines: true,
        labelStyle: AXIS_LABEL_STYLE,
        axisTitleStyle: AXIS_TITLE_STYLE,
      } as const;

      // Derive y-axis title from selected streams.
      // When daily scaling is active, append '/day' inside the unit bracket.
      const getAxisTitle = (stream: string): string => {
        const base = STREAM_Y_AXIS_TITLES[stream] ?? stream;
        if (!applyDailyScaling) return base;
        // e.g. 'Oil [bbl]' → 'Oil [bbl/day]'
        return base.replace(/\[([^\]]+)\]/, '[$1/day]');
      };

      const primaryStreams = selectedSeries.filter(
        (s) => s !== 'wgr' && s !== 'cgr'
      );
      const secondaryStreams = selectedSeries.filter(
        (s) => s === 'wgr' || s === 'cgr'
      );
      const primaryYTitle =
        primaryStreams.length === 1
          ? getAxisTitle(primaryStreams[0])
          : primaryStreams.map((s) => s).join(' / ');
      const secondaryYTitle =
        secondaryStreams.length === 1
          ? getAxisTitle(secondaryStreams[0])
          : secondaryStreams.map((s) => s).join(' / ');

      const singleStreamRatio = type === 'ratio' && selectedSeries.length === 1;

      if (type === 'ratio') {
        if (singleStreamRatio) {
          // Single-stream ratio chart: one axis with the stream's title
          surface.yAxes.add(
            new NumericAxis(wasm, {
              ...yAxisBase,
              id: PRIMARY_Y_AXIS_ID,
              axisTitle:
                STREAM_Y_AXIS_TITLES[selectedSeries[0]] ?? selectedSeries[0],
              drawMinorGridLines: false,
              growBy: yGrowBy,
              labelProvider: makeLabelProvider(formatCompact),
            })
          );
        } else {
          // Multi-stream ratio: dual axes (GOR/WOR left, WGR/CGR right)
          surface.yAxes.add(
            new NumericAxis(wasm, {
              ...yAxisBase,
              id: PRIMARY_Y_AXIS_ID,
              axisTitle: primaryYTitle || 'GOR / WOR',
              drawMinorGridLines: false,
              growBy: yGrowBy,
              labelProvider: makeLabelProvider(formatCompact),
            })
          );
          surface.yAxes.add(
            new NumericAxis(wasm, {
              ...yAxisBase,
              id: SECONDARY_Y_AXIS_ID,
              axisAlignment: EAxisAlignment.Right,
              axisTitle: secondaryYTitle || 'WGR / CGR',
              drawMajorGridLines: false,
              drawMinorGridLines: false,
              growBy: yGrowBy,
              labelProvider: makeLabelProvider(formatCompact),
            })
          );
        }
      } else if (isLogYAxis) {
        const numDecades = Math.round(
          Math.log10(yLogMax) - Math.log10(yLogMin)
        );
        surface.yAxes.add(
          new LogarithmicAxis(wasm, {
            ...yAxisBase,
            id: PRIMARY_Y_AXIS_ID,
            axisTitle: primaryYTitle || 'oil / gas / water',
            drawMinorGridLines: true,
            visibleRange: new NumberRange(yLogMin, yLogMax),
            maxAutoTicks: numDecades + 1,
            minorsPerMajor: 9,
            labelProvider: makeLabelProvider(formatLogLabel),
          })
        );
      } else {
        surface.yAxes.add(
          new NumericAxis(wasm, {
            ...yAxisBase,
            id: PRIMARY_Y_AXIS_ID,
            axisTitle: primaryYTitle || 'oil / gas / water',
            drawMinorGridLines: false,
            growBy: yGrowBy,
            labelProvider: makeLabelProvider(formatCompact),
          })
        );
      }

      // Well-count series gets its own right Y-axis so the count scale
      // (e.g. 1–90) doesn't conflict with production volume scales.
      // For all volume charts (monthly, logarithmic, cumulative) the axis
      // is logarithmic and pre-scaled to the monthly-volume log range so
      // every volume chart's right side shows the same scale regardless
      // of chart type. For ratio charts the well-count axis is linear so
      // the right-side scale matches the linear primary axis.
      if (showWellCount && wellCountData) {
        if (type === 'ratio') {
          surface.yAxes.add(
            new NumericAxis(wasm, {
              id: WELL_COUNT_Y_AXIS_ID,
              axisAlignment: EAxisAlignment.Right,
              autoRange: EAutoRange.Once,
              axisTitle: 'Well Count',
              drawMajorGridLines: false,
              drawMinorGridLines: false,
              growBy: yGrowBy,
              labelProvider: makeLabelProvider(formatCompact),
              labelStyle: AXIS_LABEL_STYLE,
              axisTitleStyle: AXIS_TITLE_STYLE,
            })
          );
        } else {
          surface.yAxes.add(
            new LogarithmicAxis(wasm, {
              id: WELL_COUNT_Y_AXIS_ID,
              axisAlignment: EAxisAlignment.Right,
              autoRange: EAutoRange.Never,
              visibleRange: new NumberRange(yLogMin, yLogMax),
              axisTitle: 'Well Count',
              drawMajorGridLines: false,
              drawMinorGridLines: false,
              logBase: 10,
              labelProvider: makeLabelProvider(formatLogLabel),
              labelStyle: AXIS_LABEL_STYLE,
              axisTitleStyle: AXIS_TITLE_STYLE,
            })
          );
        }
      }

      // =================================================================
      // 2. Build production series per well
      // =================================================================

      const productionStreams =
        type === 'ratio'
          ? ['gor', 'wor', 'wgr', 'cgr', 'watercut']
          : ['oil', 'gas', 'water'];

      for (const well of enrichedWells) {
        const productionData = well.production[volumeMap[type]];
        const firstDate = productionData[0]?.date;
        // In count mode each well is normalized to its own production start,
        // so month 1 always aligns to when that specific well came online.
        // In date mode refDate is unused so earliestDate is harmless.
        const wellRefDate = firstDate ? moment.utc(firstDate) : earliestDate;

        for (const stream of productionStreams) {
          const xValues: number[] = [];
          const yValues: number[] = [];
          const metadata: MultiWellPointMetadata[] = [];

          for (const record of productionData) {
            const yVal = record[stream as keyof typeof record] as number;
            const xVal = getXValue(record, xAxisType, wellRefDate);
            if (xVal === null || yVal === null) continue;
            if (isLogYAxis && yVal <= 0) continue;

            const monthsOnline = firstDate
              ? moment.utc(record.date).diff(moment.utc(firstDate), 'months') +
                1
              : 1;

            xValues.push(xVal);
            yValues.push(scaleY(yVal));
            metadata.push({
              isSelected: false,
              uwi: well.uwi,
              wellName: well.name,
              wellQualifier: well.wellQualifier,
              monthsOnline,
              originalDate: moment.utc(record.date).valueOf(),
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            });
          }

          if (xValues.length === 0) continue;

          const sorted = sortParallelArraysByX(xValues, yValues, metadata);

          const yAxisId =
            !singleStreamRatio && (stream === 'wgr' || stream === 'cgr')
              ? SECONDARY_Y_AXIS_ID
              : PRIMARY_Y_AXIS_ID;

          const seriesId = `well::${well.uwi}::${stream}`;

          // Per-UWI color override from color-by-attribute, falling back
          // to the default production stroke color.
          const wellStroke = uwiColorMap?.[well.uwi] ?? productionStyle.color;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sorted.xValues,
            yValues: sorted.yValues,
            dataSeriesName: `${well.name || well.uwi} ${stream}`,
            metadata: sorted.metadata,
          });

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: seriesId,
            dataSeries,
            stroke: wellStroke,
            strokeThickness: productionStyle.lineWidth,
            strokeDashArray: getDashArray(productionStyle.style),
            yAxisId,
            isVisible: selectedSeries.includes(stream as MineralType),
          });
          surface.renderableSeries.add(lineSeries);

          // Register in lookup maps
          newSeriesUwiMap.set(seriesId, well.uwi);
          seriesStreamMap.set(seriesId, stream);
          seriesColorMap.set(seriesId, wellStroke);
        }
      }

      // =================================================================
      // 3. Build type curve series
      // =================================================================

      if (showTypeCurve && enrichedTypeCurveData.length > 0) {
        const typeCurveStreams =
          type === 'ratio'
            ? ['gor', 'wor', 'wgr', 'cgr', 'watercut']
            : ['oil', 'gas', 'water'];

        for (const baseStream of typeCurveStreams) {
          const xValues: number[] = [];
          const yValues: number[] = [];
          const metadata: MultiWellPointMetadata[] = [];

          for (const record of enrichedTypeCurveData) {
            let yVal: number;
            if (type === 'ratio') {
              yVal = record[baseStream as keyof typeof record] as number;
            } else if (type === 'cumulative') {
              const key = `cumulative${baseStream
                .charAt(0)
                .toUpperCase()}${baseStream.slice(1)}` as keyof typeof record;
              yVal = record[key] as number;
            } else {
              yVal = record[baseStream as keyof typeof record] as number;
            }

            if (!yVal || yVal <= 0) continue;

            const xVal = getXValue(
              {
                date: moment.utc(record.date).format('YYYY-MM-DD'),
                oil: record.oil,
                gas: record.gas,
                water: record.water,
                cumulativeOil: record.cumulativeOil,
                cumulativeWater: record.cumulativeWater,
                cumulativeGas: record.cumulativeGas,
                materialBalanceTimeOil: record.materialBalanceTimeOil,
                materialBalanceTimeWater: record.materialBalanceTimeWater,
                materialBalanceTimeGas: record.materialBalanceTimeGas,
              } as EnrichedVolumeRecord,
              xAxisType,
              earliestProductionDate
            );

            if (xVal === null) continue;

            xValues.push(xVal);
            yValues.push(scaleY(yVal));
            metadata.push({
              isSelected: false,
              uwi: '__type_curve__',
              monthsOnline: xValues.length,
              originalDate: moment.utc(record.date).valueOf(),
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            });
          }

          if (xValues.length === 0) continue;

          const sortedTc = sortParallelArraysByX(xValues, yValues, metadata);

          const yAxisId =
            !singleStreamRatio && (baseStream === 'wgr' || baseStream === 'cgr')
              ? SECONDARY_Y_AXIS_ID
              : PRIMARY_Y_AXIS_ID;

          const tcSeriesId = `type-curve-${baseStream}`;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sortedTc.xValues,
            yValues: sortedTc.yValues,
            dataSeriesName: `Type Curve ${baseStream}`,
            metadata: sortedTc.metadata,
          });

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: tcSeriesId,
            dataSeries,
            stroke: typeCurveStyle.color,
            strokeThickness: typeCurveStyle.lineWidth,
            strokeDashArray: getDashArray(typeCurveStyle.style),
            opacity: TYPE_CURVE_OPACITY,
            yAxisId,
            isVisible:
              showTypeCurve &&
              selectedSeries.some(
                (s) => s.toLowerCase() === baseStream.toLowerCase()
              ),
          });
          surface.renderableSeries.add(lineSeries);

          // Register so the hover modifier can hit-test the type curve line
          newSeriesUwiMap.set(tcSeriesId, '__type_curve__');
          seriesStreamMap.set(tcSeriesId, baseStream);
          seriesColorMap.set(tcSeriesId, typeCurveStyle.color);
        }
      }

      // =================================================================
      // 4. Build aggregation series (solid line, same streams as type curve)
      // =================================================================

      if (showAggregation && enrichedAggregationData.length > 0) {
        const aggStreams =
          type === 'ratio'
            ? ['gor', 'wor', 'wgr', 'cgr', 'watercut']
            : ['oil', 'gas', 'water'];

        for (const baseStream of aggStreams) {
          const xValues: number[] = [];
          const yValues: number[] = [];
          const metadata: MultiWellPointMetadata[] = [];

          for (const record of enrichedAggregationData) {
            let yVal: number;
            if (type === 'ratio') {
              yVal = record[baseStream as keyof typeof record] as number;
            } else if (type === 'cumulative') {
              const key = `cumulative${baseStream
                .charAt(0)
                .toUpperCase()}${baseStream.slice(1)}` as keyof typeof record;
              yVal = record[key] as number;
            } else {
              yVal = record[baseStream as keyof typeof record] as number;
            }

            if (!yVal || yVal <= 0) continue;

            const xVal = getXValue(
              {
                date: moment.utc(record.date).format('YYYY-MM-DD'),
                oil: record.oil,
                gas: record.gas,
                water: record.water,
                cumulativeOil: record.cumulativeOil,
                cumulativeWater: record.cumulativeWater,
                cumulativeGas: record.cumulativeGas,
                materialBalanceTimeOil: record.materialBalanceTimeOil,
                materialBalanceTimeWater: record.materialBalanceTimeWater,
                materialBalanceTimeGas: record.materialBalanceTimeGas,
              } as EnrichedVolumeRecord,
              xAxisType,
              earliestProductionDate
            );

            if (xVal === null) continue;

            xValues.push(xVal);
            yValues.push(scaleY(yVal));
            metadata.push({
              isSelected: false,
              uwi: '__aggregation__',
              monthsOnline: xValues.length,
              originalDate: moment.utc(record.date).valueOf(),
              cumulativeOil: record.cumulativeOil,
              cumulativeWater: record.cumulativeWater,
              cumulativeGas: record.cumulativeGas,
              materialBalanceTimeOil: record.materialBalanceTimeOil,
              materialBalanceTimeWater: record.materialBalanceTimeWater,
              materialBalanceTimeGas: record.materialBalanceTimeGas,
            });
          }

          if (xValues.length === 0) continue;

          const sortedAgg = sortParallelArraysByX(xValues, yValues, metadata);

          const yAxisId =
            !singleStreamRatio && (baseStream === 'wgr' || baseStream === 'cgr')
              ? SECONDARY_Y_AXIS_ID
              : PRIMARY_Y_AXIS_ID;

          const aggSeriesId = `aggregation-${baseStream}`;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sortedAgg.xValues,
            yValues: sortedAgg.yValues,
            dataSeriesName: `Aggregation ${baseStream}`,
            metadata: sortedAgg.metadata,
          });

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: aggSeriesId,
            dataSeries,
            stroke: aggregationStyle.color,
            strokeThickness: aggregationStyle.lineWidth,
            strokeDashArray: getDashArray(aggregationStyle.style),
            opacity: 1,
            yAxisId,
            isVisible:
              showAggregation &&
              selectedSeries.some(
                (s) => s.toLowerCase() === baseStream.toLowerCase()
              ),
          });
          surface.renderableSeries.add(lineSeries);

          newSeriesUwiMap.set(aggSeriesId, '__aggregation__');
          seriesStreamMap.set(aggSeriesId, baseStream);
          seriesColorMap.set(aggSeriesId, aggregationStyle.color);
        }
      }

      // =================================================================
      // 4b. Forecast series are built by `renderForecast` (decoupled) — invoked
      //     at the end of this rebuild and on forecast-only changes, so a
      //     forecast response never re-runs the per-well series construction.
      // =================================================================

      // =================================================================
      // 5. Build well count series (solid black, dedicated right Y-axis)
      // =================================================================

      if (showWellCount && wellCountData) {
        const stream = selectedSeries[0];
        const countSource =
          type === 'ratio' ? wellCountData.ratios : wellCountData.volumes;

        const xValues: number[] = [];
        const yValues: number[] = [];
        const metadata: MultiWellPointMetadata[] = [];

        for (const record of countSource) {
          const yVal = record[stream as keyof typeof record] as number;
          if (!yVal || yVal <= 0) continue;

          const date = moment
            .utc(earliestProductionDate)
            .add(record.month - 1, 'months');

          const xVal = getXValue(
            {
              date: date.format('YYYY-MM-DD'),
              oil: 0,
              gas: 0,
              water: 0,
              cumulativeOil: 0,
              cumulativeWater: 0,
              cumulativeGas: 0,
              materialBalanceTimeOil: 0,
              materialBalanceTimeWater: 0,
              materialBalanceTimeGas: 0,
            } as EnrichedVolumeRecord,
            xAxisType,
            earliestProductionDate
          );

          if (xVal === null) continue;

          xValues.push(xVal);
          yValues.push(yVal);
          metadata.push({
            isSelected: false,
            uwi: '__well_count__',
            monthsOnline: record.month,
            originalDate: date.valueOf(),
          });
        }

        if (xValues.length > 0) {
          const sortedWc = sortParallelArraysByX(xValues, yValues, metadata);
          const wcSeriesId = `well-count-${String(stream)}`;

          const dataSeries = new XyDataSeries(wasm, {
            xValues: sortedWc.xValues,
            yValues: sortedWc.yValues,
            dataSeriesName: `Well Count ${String(stream)}`,
            metadata: sortedWc.metadata,
          });

          const lineSeries = new FastLineRenderableSeries(wasm, {
            id: wcSeriesId,
            dataSeries,
            stroke: wellCountStyle.color,
            strokeThickness: wellCountStyle.lineWidth,
            strokeDashArray: getDashArray(wellCountStyle.style),
            opacity: 1,
            yAxisId: WELL_COUNT_Y_AXIS_ID,
            isVisible: showWellCount,
          });
          surface.renderableSeries.add(lineSeries);

          newSeriesUwiMap.set(wcSeriesId, '__well_count__');
          seriesStreamMap.set(wcSeriesId, String(stream));
          seriesColorMap.set(wcSeriesId, wellCountStyle.color);
        }
      }

      // Update the refs for the highlight effect + hover tooltip. `renderForecast`
      // (below) adds/removes forecast entries in these same map objects.
      seriesUwiMapRef.current = newSeriesUwiMap;
      seriesStreamMapRef.current = seriesStreamMap;
      seriesColorMapRef.current = seriesColorMap;

      // =================================================================
      // 6. Chart modifiers
      // =================================================================

      // Scroll to zoom
      surface.chartModifiers.add(new MouseWheelZoomModifier());

      // Drag to pan
      surface.chartModifiers.add(new ZoomPanModifier());

      // Per-axis drag
      surface.chartModifiers.add(new XAxisDragModifier());
      surface.chartModifiers.add(new YAxisDragModifier());

      // Double-click to reset zoom
      class ResetZoomModifier extends ChartModifierBase2D {
        readonly type = 'ResetZoomModifier';
        override modifierDoubleClick(args: ModifierMouseArgs) {
          const parent = this.parentSurface;
          if (!parent) return;
          parent.zoomExtents();
          if (isLogYAxis) {
            const range = new NumberRange(yLogMin, yLogMax);
            const primaryY = parent.yAxes.getById(PRIMARY_Y_AXIS_ID);
            if (primaryY) {
              primaryY.visibleRange = range;
            }
          } else if (autoZoomYRangesRef.current.size > 0) {
            autoZoomYRangesRef.current.forEach((range, axisId) => {
              const ya = parent.yAxes.getById(axisId);
              if (ya) ya.visibleRange = range;
            });
          } else {
            parent.yAxes.asArray().forEach((ya) => {
              if (ya.id === WELL_COUNT_Y_AXIS_ID) return;
              const r = ya.visibleRange;
              if (r && r.min < 0) ya.visibleRange = new NumberRange(0, r.max);
            });
          }
          // Volume charts (monthly, logarithmic, cumulative): reset the
          // well-count log axis to its pre-scaled monthly volume range so
          // the right side stays consistent across chart types on reset.
          if (isVolumeChart) {
            const wellCountY = parent.yAxes.getById(WELL_COUNT_Y_AXIS_ID);
            if (wellCountY) {
              wellCountY.visibleRange = new NumberRange(yLogMin, yLogMax);
            }
          }

          // Restore the smart-zoomed X range (autoZoomX) instead of full extents.
          if (autoZoomXRangeRef.current) {
            const xa = parent.xAxes.getById(DEFAULT_X_AXIS_ID);
            if (xa) xa.visibleRange = autoZoomXRangeRef.current;
          }
          args.handled = true;
        }
      }
      surface.chartModifiers.add(new ResetZoomModifier());

      // Crosshair
      surface.chartModifiers.add(
        new CursorModifier({
          showTooltip: false,
          showAxisLabels: false,
          crosshairStroke: '#a8a8a8',
          crosshairStrokeDashArray: [4, 4],
        })
      );

      // Well hover + tooltip
      const hoverModifier = new WellHoverTooltipModifier();
      hoverModifier.setConfig({
        tooltipElement: tooltipRef.current,
        containerElement: chartCanvasRef.current,
        seriesUwiMap: newSeriesUwiMap,
        seriesStreamMap,
        seriesColorMap,
        chartType: type,
        onWellHover: onWellHover ?? null,
        onOverlayHover: setIsOverlayHovered,
        aggregationLabel: aggregationLabel ?? 'Aggregation',
        overlayLineWidths: {
          typeCurve: typeCurveStyle.lineWidth,
          aggregation: aggregationStyle.lineWidth,
          wellCount: wellCountStyle.lineWidth,
          forecast: forecastStyle.lineWidth,
        },
      });
      surface.chartModifiers.add(hoverModifier);

      // =================================================================
      // 7. Fit data into view
      // =================================================================

      surface.zoomExtents();

      // Volume charts (monthly, logarithmic, cumulative): re-pin the
      // well-count log axis to the monthly-volume log range. zoomExtents
      // above would otherwise refit it to the well-count data extent on
      // cumulative, making its right side disagree with monthly + log-log.
      if (isVolumeChart) {
        const wellCountY = surface.yAxes.getById(WELL_COUNT_Y_AXIS_ID);
        if (wellCountY) {
          wellCountY.visibleRange = new NumberRange(yLogMin, yLogMax);
        }
      }

      // Apply Y-axis ranges
      if (isLogYAxis) {
        const primaryY = surface.yAxes.getById(PRIMARY_Y_AXIS_ID);
        // Use provided yLogRange if available, otherwise use calculated values
        const logRange = yLogRange
          ? new NumberRange(yLogRange.min, yLogRange.max)
          : new NumberRange(yLogMin, yLogMax);
        if (primaryY) {
          primaryY.visibleRange = logRange;
        }

        // On monthly + logarithmic charts both primary and well-count axes
        // are log, so mirror the primary's range to the well-count axis and
        // keep them in lockstep on subsequent pan / zoom / drag.
        const wellCountY = surface.yAxes.getById(WELL_COUNT_Y_AXIS_ID);
        if (primaryY && wellCountY) {
          wellCountY.visibleRange = logRange;
          primaryY.visibleRangeChanged.subscribe((args) => {
            if (!args) return;
            const { min, max } = args.visibleRange;
            const current = wellCountY.visibleRange;
            if (current && current.min === min && current.max === max) return;
            wellCountY.visibleRange = new NumberRange(min, max);
          });
        }
      }

      autoZoomYRangesRef.current.clear();

      if (!isLogYAxis) {
        if (yLinearRange) {
          // Apply provided linear Y-axis range
          surface.yAxes.asArray().forEach((yAxis) => {
            if (yAxis.id !== WELL_COUNT_Y_AXIS_ID) {
              const max = yLinearRange.max ?? yAxis.visibleRange?.max ?? 100;
              yAxis.visibleRange = new NumberRange(yLinearRange.min, max);
            }
          });
        } else if (autoZoomY) {
          // Smart Y zoom: clip the visible range to a percentile of production
          // data so a few outliers don't squash the rest of the data into a
          // sliver near 0. Skip the well-count axis (independent scale).
          surface.yAxes.asArray().forEach((yAxis) => {
            if (yAxis.id === WELL_COUNT_Y_AXIS_ID) return;

            const collected: number[] = [];
            for (const s of surface.renderableSeries.asArray()) {
              if (!s.isVisible) continue;
              if (s.yAxisId !== yAxis.id) continue;
              const sId = s.id ?? '';
              // Fit to actual production data, not overlay lines
              if (
                sId.startsWith('type-curve-') ||
                sId.startsWith('aggregation-') ||
                sId.startsWith('well-count-') ||
                sId.startsWith('forecast-')
              ) {
                continue;
              }
              const ds = s.dataSeries as XyDataSeries;
              const count = ds.count();
              const yVec = ds.getNativeYValues();
              for (let i = 0; i < count; i++) {
                const v = yVec.get(i);
                if (typeof v === 'number' && isFinite(v) && v >= 0) {
                  collected.push(v);
                }
              }
            }

            if (collected.length === 0) return;
            const pVal = computePercentile(collected, autoZoomYPercentile);
            const fallbackMax = yAxis.visibleRange?.max ?? 100;
            const maxY = pVal > 0 ? pVal * AUTO_ZOOM_Y_PADDING : fallbackMax;
            const range = new NumberRange(0, maxY);
            yAxis.visibleRange = range;
            autoZoomYRangesRef.current.set(yAxis.id, range);
          });
        } else {
          // Default behavior: ensure Y-axis starts at 0
          surface.yAxes.asArray().forEach((yAxis) => {
            const range = yAxis.visibleRange;
            if (range && range.min < 0) {
              yAxis.visibleRange = new NumberRange(0, range.max);
            }
          });
        }
      }

      // Apply X-axis range
      autoZoomXRangeRef.current = null;
      const xAxis = surface.xAxes.getById(DEFAULT_X_AXIS_ID);
      if (xAxis) {
        if (isLogXAxis && xLogRange) {
          // Apply logarithmic X-axis range
          xAxis.visibleRange = new NumberRange(xLogRange.min, xLogRange.max);
        } else if (!isLogXAxis && autoZoomX) {
          // Fit X axis to the full extent of production data so no points
          // are clipped on the right side. Overlay lines (type curve,
          // aggregation, well count) are excluded so a long type-curve
          // tail can't push the axis past the actual production data.
          // When `xLinearMax` is also provided it acts as a minimum floor
          // so the axis always shows at least that range even when the
          // data extent is shorter (e.g. ratio charts default to >= 180).
          let maxX = -Infinity;
          let minX = Infinity;
          for (const s of surface.renderableSeries.asArray()) {
            if (!s.isVisible) continue;
            const sId = s.id ?? '';
            if (
              sId.startsWith('type-curve-') ||
              sId.startsWith('aggregation-') ||
              sId.startsWith('well-count-') ||
              sId.startsWith('forecast-')
            ) {
              continue;
            }
            const ds = s.dataSeries as XyDataSeries;
            const count = ds.count();
            const xVec = ds.getNativeXValues();
            for (let i = 0; i < count; i++) {
              const v = xVec.get(i);
              if (typeof v === 'number' && isFinite(v)) {
                if (v > maxX) maxX = v;
                if (v < minX) minX = v;
              }
            }
          }

          if (isFinite(maxX) && isFinite(minX)) {
            const currentMin = xAxis.visibleRange?.min ?? minX;
            const rangeMin = Math.min(currentMin, minX);
            const rangeMax =
              xLinearMax !== undefined ? Math.max(maxX, xLinearMax) : maxX;
            const range = new NumberRange(rangeMin, rangeMax);
            xAxis.visibleRange = range;
            autoZoomXRangeRef.current = range;
          }
        } else if (!isLogXAxis && xLinearMax !== undefined) {
          // Apply linear X-axis max (default zoom to show series). Stored
          // in autoZoomXRangeRef so double-click reset restores this range
          // (e.g. volume charts default to 0–180 on the count axis).
          const currentMin = xAxis.visibleRange?.min ?? 0;
          const range = new NumberRange(currentMin, xLinearMax);
          xAxis.visibleRange = range;
          autoZoomXRangeRef.current = range;
        }
      }

      surface.resumeUpdates();

      // Re-attach the forecast overlay after this full rebuild cleared it.
      renderForecast();
    }); // end scheduleChartRebuild

    return cancelRebuild;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    xAxisType,
    enrichedWells,
    type,
    earliestDate,
    decimalsForChart,
    seriesStyle,
    showTypeCurve,
    enrichedTypeCurveData,
    showAggregation,
    enrichedAggregationData,
    aggregationLabel,
    showWellCount,
    wellCountData,
    earliestProductionDate,
    onWellHover,
    selectedSeries,
    autoZoomY,
    autoZoomYPercentile,
    autoZoomX,
    uwiColorMap,
    scaleY,
    applyDailyScaling,
  ]);

  // -------------------------------------------------------------------
  // Forecast overlay effect — runs on forecast-only changes (data/visibility)
  // WITHOUT re-running the heavy per-well rebuild above.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isInitialized || !surfaceRef.current || !wasmContextRef.current)
      return;
    renderForecast();
  }, [isInitialized, renderForecast]);

  // -------------------------------------------------------------------
  // Highlight effect: adjust opacity based on highlightedUwi
  // -------------------------------------------------------------------

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    const baseThickness = productionStyle.lineWidth;
    const hoverThickness = baseThickness + HOVER_STROKE_THICKNESS_DELTA;

    for (const series of surface.renderableSeries.asArray()) {
      const id = series.id ?? '';

      // Overlay series always keep their fixed opacity — the modifier manages
      // their stroke thickness independently via the +2 hover effect.
      if (id.startsWith('type-curve-')) {
        series.opacity = TYPE_CURVE_OPACITY;
        continue;
      }
      if (
        id.startsWith('aggregation-') ||
        id.startsWith('well-count-') ||
        id.startsWith('forecast-')
      ) {
        series.opacity = 1;
        continue;
      }

      // Production wells
      if (isOverlayHovered) {
        // An overlay series is hovered in this chart → dim all production wells
        series.opacity = DIMMED_OPACITY;
        (series as FastLineRenderableSeries).strokeThickness = baseThickness;
        continue;
      }

      if (!highlightedUwi) {
        series.opacity = 1;
        (series as FastLineRenderableSeries).strokeThickness = baseThickness;
        continue;
      }

      const uwi = seriesUwiMapRef.current.get(id);
      const isHighlighted = uwi === highlightedUwi;
      series.opacity = isHighlighted ? 1 : DIMMED_OPACITY;
      (series as FastLineRenderableSeries).strokeThickness = isHighlighted
        ? hoverThickness
        : baseThickness;
    }
  }, [highlightedUwi, isOverlayHovered, productionStyle.lineWidth]);

  // -------------------------------------------------------------------
  // Series visibility toggle (lightweight, no rebuild)
  // -------------------------------------------------------------------

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    surface.renderableSeries.asArray().forEach((series) => {
      const id = series.id ?? '';

      if (id.startsWith('type-curve-')) {
        const baseStream = id.replace('type-curve-', '');
        series.isVisible =
          showTypeCurve &&
          selectedSeries.some(
            (s) => s.toLowerCase() === baseStream.toLowerCase()
          );
        return;
      }

      if (id.startsWith('aggregation-')) {
        const baseStream = id.replace('aggregation-', '');
        series.isVisible =
          showAggregation &&
          selectedSeries.some(
            (s) => s.toLowerCase() === baseStream.toLowerCase()
          );
        return;
      }

      if (id.startsWith('well-count-')) {
        series.isVisible = showWellCount;
        return;
      }

      if (id.startsWith('forecast-')) {
        const baseStream = id.replace('forecast-', '');
        series.isVisible =
          showForecast &&
          selectedSeries.some(
            (s) => s.toLowerCase() === baseStream.toLowerCase()
          );
        return;
      }

      // Well series: ID is "well::{uwi}::{stream}"
      const parts = id.split('::');
      const stream = parts[parts.length - 1] ?? '';
      series.isVisible = selectedSeries.includes(stream as MineralType);
    });
  }, [
    selectedSeries,
    showTypeCurve,
    showAggregation,
    showWellCount,
    showForecast,
  ]);

  // -------------------------------------------------------------------
  // Responsive resizing
  // -------------------------------------------------------------------

  const handleResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width } = entries[entries.length - 1].contentRect;
        const divEl = document.getElementById(chartDivId);
        if (divEl) {
          divEl.style.width = `${width}px`;
          // Re-asserting `height` here is what pins the canvas on every resize,
          // so `fillHeight` has to be honored in this branch too — otherwise the
          // flexed height is stamped back to the fixed one.
          divEl.style.height = fillHeight ? '100%' : `${height}px`;
        }
      }, 150),
    [chartDivId, height, fillHeight]
  );

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartContainerRef.current);
    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <div ref={chartContainerRef} className={style.chartContainer}>
      {header}

      {exportConfig && (
        <SciChartExportImageButton
          iconOnly
          surfaceRef={surfaceRef}
          containerRef={chartCanvasRef}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          elementHooks={exportElementHooks}
        />
      )}

      {/* Canvas wrapper — tooltip and overlays are positioned relative to this
          so their coordinates match args.mousePoint from SciChart */}
      <div
        ref={chartCanvasRef}
        style={
          fillHeight
            ? { position: 'relative', flex: '1 1 0', minHeight: 0 }
            : { position: 'relative' }
        }
      >
        <div
          id={chartDivId}
          style={{
            width: '100%',
            height: fillHeight ? '100%' : `${height}px`,
          }}
        />

        <div ref={tooltipRef} className={style.tooltip} />

        {isLoading && <div className={style.loadingOverlay}>Loading...</div>}
      </div>

      {footer}
    </div>
  );
};
